import type { Dispatch, SetStateAction } from "react";
import React from "react";
import { Box, Grid, Option } from "@mui/joy";
import DarkInput from "components/common/DarkInput";
import DarkSelect from "components/common/DarkSelect";
import DarkTypography from "components/common/DarkTypography";
import DarkCheckbox from "components/common/DarkCheckbox";
import { useAuth } from "contexts/api";
import { useUsersModuleData } from "contexts/app/usersModuleData";
import CommonModal from "components/common/CommonModal";

type UserInviteComponentProps = {
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onCreated?: () => void;
};

type AccountMode = "with_account" | "without_account";

export default function UserInviteComponent({ setIsOpen, onCreated }: UserInviteComponentProps) {
  const [accountMode, setAccountMode] = React.useState<AccountMode>("with_account");
  const [email, setEmail] = React.useState<string>("");
  const [firstName, setFirstName] = React.useState<string>("");
  const [lastName, setLastName] = React.useState<string>("");
  const [phoneNumber, setPhoneNumber] = React.useState<string>("");
  const [isGuide, setIsGuide] = React.useState(false);
  const [role, setRole] = React.useState<number | null>(null);
  const { createDirectoryUser } = useAuth();
  const { permissionsCatalog } = useUsersModuleData();
  const rolesFromCatalog = permissionsCatalog?.roles;
  const withAccount = accountMode === "with_account";

  React.useEffect(() => {
    if (!role && rolesFromCatalog && rolesFromCatalog.length > 0) {
      setRole(rolesFromCatalog[0].id);
    }
  }, [role, rolesFromCatalog]);

  const handleConfirm = async () => {
    const first = firstName.trim();
    const last = lastName.trim();

    if (!first && !last) return;

    if (withAccount) {
      const emailOk = /.+@.+\..+/.test(email);
      if (!emailOk || role === null) return;

      await createDirectoryUser({
        first_name: first,
        last_name: last,
        email,
        role_id: role,
      });
      setIsOpen(false);
      onCreated?.();

      return;
    }

    await createDirectoryUser({
      first_name: first,
      last_name: last,
      phone_number: phoneNumber.trim(),
      is_guide: isGuide,
    });
    setIsOpen(false);
    onCreated?.();
  };

  return (
    <CommonModal
      open
      onClose={() => setIsOpen(false)}
      title="Ajouter un utilisateur"
      size="md"
      onConfirm={handleConfirm}
      confirmLabel={withAccount ? "Inviter" : "Créer"}
    >
      <Box sx={{ textAlign: "start" }}>
        <Grid container>
          <Grid xs={12}>
            <DarkSelect
              placeholder="Type"
              value={accountMode}
              onChange={(_, newValue) =>
                setAccountMode((newValue as AccountMode) ?? "with_account")
              }
              sx={{ width: "100%", marginBottom: "20px" }}
              aria-label="Avec ou sans compte"
            >
              <Option value="with_account">Avec compte</Option>
              <Option value="without_account">Sans compte</Option>
            </DarkSelect>
            {withAccount ? (
              <DarkInput
                placeholder="Email"
                sx={{ width: "100%", marginBottom: "20px" }}
                onChange={(e) => setEmail(e.target.value.toLowerCase())}
                value={email}
              />
            ) : null}
            <DarkInput
              placeholder="Prénom"
              sx={{ width: "100%", marginBottom: "20px" }}
              onChange={(e) => setFirstName(e.target.value)}
              value={firstName}
            />
            <DarkInput
              placeholder="Nom"
              sx={{ width: "100%", marginBottom: "20px" }}
              onChange={(e) => setLastName(e.target.value)}
              value={lastName}
            />
            {!withAccount ? (
              <>
                <DarkInput
                  placeholder="Téléphone"
                  sx={{ width: "100%", marginBottom: "20px" }}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  value={phoneNumber}
                />
                <DarkCheckbox
                  label="Encadrant"
                  checked={isGuide}
                  onChange={(e) => setIsGuide(e.target.checked)}
                  sx={{ mb: 2 }}
                />
              </>
            ) : (
              <>
                <DarkSelect
                  placeholder="Rôle"
                  value={role}
                  onChange={(e, newValue) => setRole((newValue as number) ?? null)}
                  sx={{ width: "100%", marginBottom: "20px" }}
                  disabled={!rolesFromCatalog || rolesFromCatalog.length === 0}
                >
                  {rolesFromCatalog &&
                    rolesFromCatalog.length > 0 &&
                    rolesFromCatalog.map((r) => (
                      <Option key={r.id} value={r.id}>
                        {r.name}
                      </Option>
                    ))}
                </DarkSelect>
                {!rolesFromCatalog || rolesFromCatalog.length === 0 ? (
                  <DarkTypography level="body-sm" colorVariant="muted" sx={{ mt: -1, mb: 2 }}>
                    Aucun rôle disponible. Créez des rôles dans Utilisateurs → Rôles.
                  </DarkTypography>
                ) : null}
              </>
            )}
          </Grid>
        </Grid>
      </Box>
    </CommonModal>
  );
}
